import React, { useState, useEffect } from "react";
import debounce from 'lodash/debounce';

const ViewTranslations = ({ org_id, project_id, language_code }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedTranslations, setEditedTranslations] = useState({});
  const [savingStates, setSavingStates] = useState({});
  const [ttsGeneratingStates, setTtsGeneratingStates] = useState({});
  const [translatingStates, setTranslatingStates] = useState({});
  const [audioRefreshKeys, setAudioRefreshKeys] = useState({});
  const [project, setProject] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [debouncedFunctions] = useState(() => ({}));
  const [addedPrompt, setAddedPrompt] = useState('');
  const [isPromptExpanded, setIsPromptExpanded] = useState(true);
  const [voices, setVoices] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [selectedElevenLabsApi, setSelectedElevenLabsApi] = useState('multilingual_v2');
  const [translationProvider, setTranslationProvider] = useState('openai');
  const [bulkRetranslating, setBulkRetranslating] = useState(false);
  const [reviewStates, setReviewStates] = useState({});
  const [qualityTags, setQualityTags] = useState({});

  const providerStorageKey = `translation_provider_${language_code}`;

  useEffect(() => {
    const stored = localStorage.getItem(providerStorageKey);
    if (stored === 'openai' || stored === 'google') {
      setTranslationProvider(stored);
    }
    const storedApi = localStorage.getItem('eleven_labs_api');
    if (storedApi === 'multilingual_v2' || storedApi === 'eleven_v3') {
      setSelectedElevenLabsApi(storedApi);
    }
  }, [language_code]);

  const handleElevenLabsApiChange = (api) => {
    setSelectedElevenLabsApi(api);
    localStorage.setItem('eleven_labs_api', api);
  };

  useEffect(() => {
    fetchTranslations();
    fetchProject();
    fetchVoices();
  }, [org_id, project_id, language_code]);

  const handleTranslationProviderChange = (provider) => {
    setTranslationProvider(provider);
    localStorage.setItem(providerStorageKey, provider);
  };

  const fetchVoices = async () => {
    try {
      const response = await fetch('/api_super_admin/voices');
      if (!response.ok) throw new Error('Failed to fetch voices');
      const data = await response.json();
      setVoices(data);
    } catch (error) {
      console.error('Error fetching voices:', error);
    }
  };

  const fetchProject = async () => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}`);
      if (!response.ok) throw new Error('Failed to fetch project');
      const projectData = await response.json();
      setProject(projectData);
    } catch (err) {
      console.error('Error fetching project:', err);
    }
  };

  const fetchTranslations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/translations?language_code=${language_code}`);
      if (!response.ok) throw new Error('Failed to fetch translations');
      const result = await response.json();
      setData(result);
      setError(null);
      // Reset edited translations when data is refreshed
      setEditedTranslations({});
    } catch (err) {
      setError(err.message);
      console.error('Error fetching translations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTranslationChange = (englishTranscriptId, newTranslation) => {
    setEditedTranslations(prev => ({
      ...prev,
      [englishTranscriptId]: newTranslation
    }));

    // Cancel previous debounced call if it exists
    if (debouncedFunctions[englishTranscriptId]) {
      debouncedFunctions[englishTranscriptId].cancel();
    }

    // Create new debounced function if it doesn't exist
    if (!debouncedFunctions[englishTranscriptId]) {
      debouncedFunctions[englishTranscriptId] = debounce(async (translatedText) => {
        try {
          setSavingStates(prev => ({ ...prev, [englishTranscriptId]: true }));
          
          const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/update_translation`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
            },
            body: JSON.stringify({
              language_code: language_code,
              english_transcript_id: englishTranscriptId,
              translated_text: translatedText
            })
          });

          if (!response.ok) {
            throw new Error('Failed to save translation');
          }

          const result = await response.json();
          
          // Update local data
          if (data && data.translations) {
            const updatedTranslations = data.translations.map(t => {
              if (t.id === englishTranscriptId) {
                return {
                  ...t,
                  translated_text: result.translation.translated_text,
                  translation_status: result.translation.status
                };
              }
              return t;
            });
            setData({
              ...data,
              translations: updatedTranslations
            });
          }
          
          setSavingStates(prev => ({ ...prev, [englishTranscriptId]: false }));
        } catch (error) {
          console.error('Failed to save translation:', error);
          setSavingStates(prev => ({ ...prev, [englishTranscriptId]: false }));
        }
      }, 1000);
    }

    // Call the debounced function
    debouncedFunctions[englishTranscriptId](newTranslation);
  };

  const handleGenerateTTS = async (englishTranscriptId) => {
    try {
      setTtsGeneratingStates(prev => ({ ...prev, [englishTranscriptId]: true }));
      
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/generate_single_translation_tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          language_code: language_code,
          english_transcript_id: englishTranscriptId,
          voice_id: selectedVoiceId || null,
          eleven_labs_api: selectedElevenLabsApi
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate TTS');
      }
      
      // Update local data
      if (data && data.translations) {
        const updatedTranslations = data.translations.map(t => {
          if (t.id === englishTranscriptId) {
            return {
              ...t,
              mp3_generated: result.translation.mp3_generated,
              mp3_url: result.translation.mp3_url
            };
          }
          return t;
        });
        setData({
          ...data,
          translations: updatedTranslations
        });
      }
      
      // Update refresh key to force audio player to reload
      setAudioRefreshKeys(prev => ({ ...prev, [englishTranscriptId]: Date.now() }));
      setTtsGeneratingStates(prev => ({ ...prev, [englishTranscriptId]: false }));
    } catch (error) {
      console.error('Failed to generate TTS:', error);
      alert('Failed to generate TTS: ' + error.message);
      setTtsGeneratingStates(prev => ({ ...prev, [englishTranscriptId]: false }));
    }
  };

  const handleGenerateTranslation = async (englishTranscriptId) => {
    try {
      setTranslatingStates(prev => ({ ...prev, [englishTranscriptId]: true }));
      
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/generate_single_translation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          language_code: language_code,
          english_transcript_id: englishTranscriptId,
          translation_provider: translationProvider,
          added_prompt: translationProvider === 'openai' ? addedPrompt : ''
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate translation');
      }

      const result = await response.json();
      
      // Update local data
      if (data && data.translations) {
        const updatedTranslations = data.translations.map(t => {
          if (t.id === englishTranscriptId) {
            return {
              ...t,
              translated_text: result.translation.translated_text,
              translation_status: result.translation.status
            };
          }
          return t;
        });
        setData({
          ...data,
          translations: updatedTranslations
        });
      }
      
      setTranslatingStates(prev => ({ ...prev, [englishTranscriptId]: false }));
    } catch (error) {
      console.error('Failed to generate translation:', error);
      alert('Failed to generate translation: ' + error.message);
      setTranslatingStates(prev => ({ ...prev, [englishTranscriptId]: false }));
    }
  };

  const handleReviewTranslation = async (englishTranscriptId, targetLang) => {
    const row = data?.translations?.find(t => t.id === englishTranscriptId);
    const text = editedTranslations[englishTranscriptId] !== undefined
      ? editedTranslations[englishTranscriptId]
      : (row?.translated_text || '');

    if (!text.trim()) {
      alert('Translated text is required to review');
      return;
    }

    const loadingKey = targetLang === 'en' ? 'loadingEn' : 'loadingFil';
    const resultKey = targetLang === 'en' ? 'en' : 'fil';

    setReviewStates(prev => ({
      ...prev,
      [englishTranscriptId]: {
        ...(prev[englishTranscriptId] || {}),
        [loadingKey]: true,
        error: null
      }
    }));

    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/review_translation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          text,
          target_lang: targetLang,
          source_lang: language_code || 'auto',
          translation_provider: translationProvider
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to review translation');
      }

      const result = await response.json();
      setReviewStates(prev => ({
        ...prev,
        [englishTranscriptId]: {
          ...(prev[englishTranscriptId] || {}),
          [loadingKey]: false,
          [resultKey]: result.translated_text,
          error: null
        }
      }));
    } catch (error) {
      console.error('Failed to review translation:', error);
      setReviewStates(prev => ({
        ...prev,
        [englishTranscriptId]: {
          ...(prev[englishTranscriptId] || {}),
          [loadingKey]: false,
          error: error.message
        }
      }));
      alert('Failed to review translation: ' + error.message);
    }
  };

  const handleQualityTag = (englishTranscriptId, tag) => {
    setQualityTags(prev => {
      if (prev[englishTranscriptId] === tag) {
        const next = { ...prev };
        delete next[englishTranscriptId];
        return next;
      }
      return { ...prev, [englishTranscriptId]: tag };
    });
  };

  const handleRetranslateAll = async () => {
    const providerLabel = translationProvider === 'google' ? 'Google Translate' : 'OpenAI (GPT)';
    if (!window.confirm(`Re-translate all rows using ${providerLabel}? Existing translations will be overwritten.`)) {
      return;
    }

    try {
      setBulkRetranslating(true);
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/generate_translations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          language_code: language_code,
          translation_provider: translationProvider,
          force: true,
          added_prompt: translationProvider === 'openai' ? addedPrompt : ''
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to re-translate all');
      }

      await fetchTranslations();
    } catch (error) {
      console.error('Failed to re-translate all:', error);
      alert('Failed to re-translate all: ' + error.message);
    } finally {
      setBulkRetranslating(false);
    }
  };

  const handleKeyDown = (e, currentIndex) => {
    const textareas = document.querySelectorAll('.translation-text-textarea');
    
    switch (e.key) {
      case 'ArrowUp':
        if (currentIndex > 0) {
          textareas[currentIndex - 1].focus();
        }
        break;
      case 'ArrowDown':
        if (currentIndex < textareas.length - 1) {
          textareas[currentIndex + 1].focus();
        }
        break;
      case 'Enter':
        if (!e.shiftKey) {
          e.preventDefault();
          if (currentIndex < textareas.length - 1) {
            textareas[currentIndex + 1].focus();
          }
        }
        break;
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!project) {
      alert('Project data not loaded');
      return;
    }

    try {
      setUpdatingStatus(true);
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          project: {
            ...project,
            project_status: newStatus
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update project status');
      }

      // Close the window and redirect parent to the project page
      const projectUrl = `/super_admin/orgs/${org_id}/projects/${project_id}`;
      if (window.opener) {
        // If opened in a new window, redirect the opener and close this window
        window.opener.location.href = projectUrl;
        window.close();
      } else {
        // If not in a popup, just redirect
        window.location.href = projectUrl;
      }
    } catch (error) {
      console.error('Error updating project status:', error);
      alert('Failed to update project status: ' + error.message);
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return <div className="text-center p-4">Loading translations...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">Error: {error}</div>;
  }

  if (!data || !data.translations) {
    return <div className="alert alert-info">No translations found for this language.</div>;
  }

  return (
    <div className="view-translations">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>
          {data.language_name} ({data.language_code}) Translations
        </h5>
        <div className="d-flex gap-2 align-items-center">
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto', minWidth: '180px' }}
            value={translationProvider}
            onChange={(e) => handleTranslationProviderChange(e.target.value)}
            title="Translation provider for translate and re-translate actions"
          >
            <option value="openai">OpenAI (GPT)</option>
            <option value="google">Google Translate</option>
          </select>
          <button
            className="btn btn-sm btn-outline-warning"
            onClick={handleRetranslateAll}
            disabled={bulkRetranslating}
            title="Re-translate all rows using the selected provider"
          >
            {bulkRetranslating ? 'Re-translating...' : 'Re-translate All'}
          </button>
          <button 
            className={`btn btn-sm ${editMode ? 'btn-warning' : 'btn-primary'}`}
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? 'Exit Edit Mode' : 'Edit Mode'}
          </button>
          {/* ElevenLabs API Selector */}
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto', minWidth: '140px' }}
            value={selectedElevenLabsApi}
            onChange={(e) => handleElevenLabsApiChange(e.target.value)}
            title="ElevenLabs TTS model"
          >
            <option value="multilingual_v2">Multilingual v2</option>
            <option value="eleven_v3">Eleven v3</option>
          </select>
          {/* Voice Selector */}
          <select 
            className="form-select form-select-sm" 
            style={{ width: 'auto', minWidth: '150px' }}
            value={selectedVoiceId}
            onChange={(e) => setSelectedVoiceId(e.target.value)}
          >
            <option value="">Default Voice</option>
            {voices.map((voice) => (
              <option key={voice.id} value={voice.voice_id}>
                {voice.name}
              </option>
            ))}
          </select>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={fetchTranslations}
          >
            Refresh
          </button>
          <div className="btn-group" role="group">
            <button
              className="btn btn-sm btn-info"
              onClick={() => handleStatusChange('generate_tts')}
              disabled={updatingStatus}
              title="Switch to Generate TTS view"
            >
              {updatingStatus ? 'Updating...' : 'Go to Generate TTS'}
            </button>
            <button
              className="btn btn-sm btn-success"
              onClick={() => handleStatusChange('per_language')}
              disabled={updatingStatus}
              title="Switch to Per Language view"
            >
              {updatingStatus ? 'Updating...' : 'Go to Per Language'}
            </button>
          </div>
        </div>
      </div>

      {/* Floating Collapsible Additional Prompt Section (OpenAI only) */}
      {translationProvider === 'openai' && (
      <div
        style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          width: '300px',
          backgroundColor: '#ffd2fa',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000,
          border: '1px solid #dee2e6',
          overflow: 'hidden'
        }}
      >
        <div 
          onClick={() => setIsPromptExpanded(!isPromptExpanded)}
          style={{
            padding: '10px 15px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: isPromptExpanded ? '#ffc4f5' : '#ffd2fa',
            transition: 'background-color 0.2s'
          }}
        >
          <label style={{ fontWeight: 'bold', margin: 0, cursor: 'pointer' }}>
            Additional Prompt (optional):
          </label>
          <span style={{ 
            transform: isPromptExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            fontSize: '12px'
          }}>
            ▼
          </span>
        </div>
        {isPromptExpanded && (
          <div style={{ padding: '0 15px 15px 15px' }}>
            <textarea
              id="added-prompt"
              className="form-control"
              rows="3"
              placeholder="Enter additional instructions for translation..."
              value={addedPrompt}
              onChange={(e) => setAddedPrompt(e.target.value)}
              style={{ fontSize: '12px', resize: 'vertical' }}
            />
            <small className="form-text text-muted" style={{ fontSize: '11px' }}>
              This prompt will be added to individual translation requests.
            </small>
          </div>
        )}
      </div>
      )}

      <div className="table-responsive">
        <table className="table table-bordered table-striped">
          <thead className="table-dark">
            <tr>
              <th style={{ width: '60px' }}>#</th>
              <th style={{ width: '200px' }}>Timeframe</th>
              <th>Original Transcript</th>
              <th>English Text</th>
              <th style={{ width: '300px' }}>Translated Text</th>
              <th style={{ width: '120px' }}>Status</th>
              <th style={{ width: '280px' }}>Review</th>
              <th style={{ width: '160px' }}>Actions</th>
              <th style={{ width: '200px' }}>TTS</th>
            </tr>
          </thead>
          <tbody>
            {data.translations.map((item, index) => {
              const currentValue = editedTranslations[item.id] !== undefined 
                ? editedTranslations[item.id] 
                : (item.translated_text || '');
              const audioUrl = item.mp3_url 
                ? `${item.mp3_url}${item.mp3_url.includes('?') ? '&' : '?'}t=${audioRefreshKeys[item.id] || ''}`
                : null;
              
              return (
                <tr key={item.id}>
                  <td>{item.sequence_number}</td>
                  <td>{item.timeframe}</td>
                  <td>
                    <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                      {item.original_transcript_text || '-'}
                    </div>
                  </td>
                  <td>
                    <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                      {item.english_text || item.original_text || '-'}
                    </div>
                  </td>
                  <td className={editMode ? "p-1" : ""}>
                    {editMode ? (
                      <>
                        <textarea
                          className="form-control translation-text-textarea border-0 rounded-0"
                          style={{ minHeight: '100px', maxHeight: '200px', resize: 'vertical' }}
                          value={currentValue}
                          onChange={(e) => handleTranslationChange(item.id, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          placeholder={item.translation_status === 'in_progress' ? 'Translating...' : 
                                       item.translation_status === 'failed' ? 'Translation failed' : 
                                       'Not translated'}
                        />
                        {savingStates[item.id] && (
                          <small className="text-muted">Saving...</small>
                        )}
                      </>
                    ) : (
                      <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                        {item.translated_text || (
                          <span className="text-muted">
                            {item.translation_status === 'in_progress' ? 'Translating...' : 
                             item.translation_status === 'failed' ? 'Translation failed' : 
                             'Not translated'}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {item.translation_status === 'completed' && (
                      <span className="badge bg-success">Completed</span>
                    )}
                    {item.translation_status === 'in_progress' && (
                      <span className="badge bg-warning">In Progress</span>
                    )}
                    {item.translation_status === 'failed' && (
                      <span className="badge bg-danger">Failed</span>
                    )}
                    {item.translation_status === 'pending' && (
                      <span className="badge bg-secondary">Pending</span>
                    )}
                    {!item.translation_status && (
                      <span className="badge bg-info">Not Started</span>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const review = reviewStates[item.id] || {};
                      const qualityTag = qualityTags[item.id];
                      const hasContent = review.en || review.fil || review.loadingEn || review.loadingFil || review.error || qualityTag;
                      if (!hasContent) {
                        return <span className="text-muted">-</span>;
                      }
                      return (
                        <div className="d-flex flex-column gap-1" style={{ fontSize: '12px' }}>
                          {qualityTag && (
                            <span className={`badge ${qualityTag === 'good' ? 'bg-success' : 'bg-danger'}`}>
                              {qualityTag === 'good' ? 'Good' : 'Bad'}
                            </span>
                          )}
                          {(review.loadingEn || review.en) && (
                            <div>
                              <strong>EN:</strong>{' '}
                              {review.loadingEn ? (
                                <span className="text-muted">Reviewing...</span>
                              ) : (
                                <div style={{ maxHeight: '80px', overflowY: 'auto' }}>{review.en}</div>
                              )}
                            </div>
                          )}
                          {(review.loadingFil || review.fil) && (
                            <div>
                              <strong>FIL:</strong>{' '}
                              {review.loadingFil ? (
                                <span className="text-muted">Reviewing...</span>
                              ) : (
                                <div style={{ maxHeight: '80px', overflowY: 'auto' }}>{review.fil}</div>
                              )}
                            </div>
                          )}
                          {review.error && (
                            <small className="text-danger">{review.error}</small>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      <button
                        className={`btn btn-sm ${item.translated_text ? 'btn-outline-primary' : 'btn-primary'}`}
                        onClick={() => handleGenerateTranslation(item.id)}
                        disabled={translatingStates[item.id]}
                        title={item.translated_text ? 'Re-translate this row' : 'Translate this row'}
                      >
                        {translatingStates[item.id] ? 'Translating...' : (item.translated_text ? 'Re-translate' : 'Translate')}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleReviewTranslation(item.id, 'en')}
                        disabled={!currentValue.trim() || reviewStates[item.id]?.loadingEn}
                        title={!currentValue.trim() ? 'Translated text required' : 'Review translation in English'}
                      >
                        {reviewStates[item.id]?.loadingEn ? 'Reviewing...' : 'Review in English'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleReviewTranslation(item.id, 'tl')}
                        disabled={!currentValue.trim() || reviewStates[item.id]?.loadingFil}
                        title={!currentValue.trim() ? 'Translated text required' : 'Review translation in Filipino'}
                      >
                        {reviewStates[item.id]?.loadingFil ? 'Reviewing...' : 'Review in Filipino'}
                      </button>
                      <div className="d-flex gap-1">
                        <button
                          className={`btn btn-sm ${qualityTags[item.id] === 'good' ? 'btn-success' : 'btn-outline-success'}`}
                          onClick={() => handleQualityTag(item.id, 'good')}
                          title="Mark as Good (optional)"
                        >
                          Good
                        </button>
                        <button
                          className={`btn btn-sm ${qualityTags[item.id] === 'bad' ? 'btn-danger' : 'btn-outline-danger'}`}
                          onClick={() => handleQualityTag(item.id, 'bad')}
                          title="Mark as Bad (optional)"
                        >
                          Bad
                        </button>
                      </div>
                    </div>
                  </td>
                  <td>
                    {editMode ? (
                      <div className="d-flex flex-column gap-1">
                        {item.mp3_generated && item.mp3_url ? (
                          <>
                            <audio 
                              key={`audio-${item.id}-${audioRefreshKeys[item.id] || 0}`}
                              controls 
                              className="w-100"
                              style={{ maxWidth: '200px', height: '32px' }}
                            >
                              <source src={audioUrl} type="audio/mpeg" />
                              Your browser does not support the audio element.
                            </audio>
                            <a 
                              href={audioUrl} 
                              className="btn btn-sm btn-success"
                              target="_blank"
                              download
                            >
                              Download
                            </a>
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleGenerateTTS(item.id)}
                              disabled={ttsGeneratingStates[item.id] || !item.translated_text}
                              title={!item.translated_text ? 'Translation required' : 'Regenerate TTS'}
                            >
                              {ttsGeneratingStates[item.id] ? 'Generating...' : 'Regenerate'}
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleGenerateTTS(item.id)}
                            disabled={ttsGeneratingStates[item.id] || !item.translated_text}
                            title={!item.translated_text ? 'Translation required' : 'Generate TTS'}
                          >
                            {ttsGeneratingStates[item.id] ? 'Generating...' : 'Generate TTS'}
                          </button>
                        )}
                        {!item.translated_text && (
                          <small className="text-muted">Translation required</small>
                        )}
                      </div>
                    ) : (
                      <>
                        {item.mp3_generated && item.mp3_url ? (
                          <a 
                            href={audioUrl} 
                            className="btn btn-sm btn-success"
                            target="_blank"
                            download
                          >
                            Download
                          </a>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <div className="alert alert-info">
          <strong>Total:</strong> {data.translations.length} transcripts
          {' • '}
          <strong>Translated:</strong> {data.translations.filter(t => t.translation_status === 'completed').length}
          {' • '}
          <strong>In Progress:</strong> {data.translations.filter(t => t.translation_status === 'in_progress').length}
          {' • '}
          <strong>Failed:</strong> {data.translations.filter(t => t.translation_status === 'failed').length}
        </div>
      </div>
    </div>
  );
};

export default ViewTranslations;

